import createSagaMiddleware, { SagaMiddleware } from 'redux-saga';
import { createStore, applyMiddleware, Middleware, combineReducers, ReducersMapObject, Reducer } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { IModuleContext } from '../models/IModuleContext';
import { IModuleStoreBuilder } from '../models/IModuleStoreBuilder';
import { IReducerRegistry } from '../models/IReducerRegistry';
import { IModuleStoreBuilderResult } from '../models/IModuleStoreBuilderResult';

/**
 * ModuleStoreBuilder: Implementation for redux store builder.
 */
export class ModuleStoreBuilder<MT extends IModuleContext, T extends {}> implements IModuleStoreBuilder<MT, T> {
  private readonly initialState: T;
  private readonly reducerRegistery: IReducerRegistry;

  private middlewares: Middleware[] = [];
  private sagaMiddleware: SagaMiddleware | undefined;
  private context: MT = {} as MT;
  private debug: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rootSagas: any[] = [];

  public constructor(reducerRegistery: IReducerRegistry, initialState: T, debug: boolean = false) {
    this.debug = debug;
    this.reducerRegistery = reducerRegistery;
    this.initialState = {
      ...initialState,
    };
  }

  /**
   * configure saga middleware with initial context
   * @param context: implementation of IModuleContext
   * @returns IModuleStoreBuilder<MT, T>: stored builder object
   */
  public configureSaga(context: MT): IModuleStoreBuilder<MT, T> {
    this.context = context;
    this.sagaMiddleware = createSagaMiddleware({
      context,
    });
    this.middlewares.push(this.sagaMiddleware);

    return this;
  }

  /**
   * add redux middleware
   * @param middleware: Middleware
   * @returns IModuleStoreBuilder<MT, T>: stored builder object
   */
  public addMiddleware(middleware: Middleware): IModuleStoreBuilder<MT, T> {
    this.middlewares.push(middleware);

    return this;
  }

  /**
   * Add root sagas
   * @param sagas: any[]
   * @returns IModuleStoreBuilder<MT, T>: stored builder object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public addRootSagas(sagas: any[]): IModuleStoreBuilder<MT, T> {
    this.rootSagas = sagas;

    return this;
  }

  /**
   * Build redux store and context.
   * @returns IModuleStoreBuilderResult<MT, T>: store and context object.
   */
  public build(): IModuleStoreBuilderResult<MT, T> {
    if (!this.sagaMiddleware) throw new Error('Saga middleware was not configured.');
    if (!this.context) throw new Error('Context must be configured using configureContext method');

    const reducers = this.combine(
      this.reducerRegistery.getReducers(),
      this.reducerRegistery.getDynamicReducers(),
      this.initialState
    );
    const store = createStore(
      reducers,
      this.initialState,
      !this.debug ? applyMiddleware(...this.middlewares) : composeWithDevTools(applyMiddleware(...this.middlewares))
    );

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    // we've already checked that sagaMiddleware is not null
    this.rootSagas.map((saga) => this.sagaMiddleware!.run(saga));

    return {
      store,
      reducerRegistry: this.reducerRegistery,
      runSaga: this.sagaMiddleware.run,
      context: this.context,
    };
  }

  private combine(
    reducers: ReducersMapObject,
    dynamicReducers: ReducersMapObject,
    currentState: T | null = null
  ): Reducer {
    const reducerNames = Object.keys(reducers);
    const dynamicReducerNames = Object.keys(dynamicReducers);

    Object.keys(currentState || this.initialState).forEach((item: string): void => {
      if (reducerNames.indexOf(item) === -1) {
        // eslint-disable-next-line @typescript-eslint/ban-types
        reducers[item] = (state = null): {} => state;
      }
    });

    Object.keys((currentState && (currentState as any).dynamic) || {}).forEach((item: string) => {
      if (dynamicReducerNames.indexOf(item) === -1) {
        // eslint-disable-next-line @typescript-eslint/ban-types
        dynamicReducers[item] = (state = null): {} => state;
      }
    });

    return combineReducers(reducers);
  }
}
